import { useCallback, useRef, useState } from "react"
import type { DealDrawer as DealDrawerData } from "@/types/v2/conversation"
import {
  replaceDealProp,
  type DealPropKey,
} from "@/lib/deal-prop-update"
import { getCsrfToken } from "@/lib/csrf"

// Inline-edit a deal field from the v2 slideover or
// mobile detail page. Mirrors `useStageMove`: applies
// an optimistic update via `router.replaceProp`, hits
// the PATCH endpoint, reverts on failure, and lets the
// ActionCable `deal.updated` broadcast reconcile any
// derived fields (activity timeline, status, etc.).
//
// The hook is field-agnostic — callers pass a partial
// `DealDrawer` patch and a `payload` that matches the
// server's update params. Keeping the patch separate
// from the payload lets the UI optimistically render
// formatted strings the server would otherwise have to
// recompute (e.g. "$48,000" alongside the raw amount).

// Owner is no longer edited through this hook — deal owners
// are multi-select (CON-218) and PATCH through
// `updateDealAssignment` instead.
export type DealFieldName = "name" | "value" | "close_date"

export interface DealUpdatePayload {
  name?: string
  value?: string | null
  close_date?: string | null
}

interface UseDealUpdateOptions {
  deal: DealDrawerData
  dealPropKey: DealPropKey
  // Called after a successful PATCH so the caller can
  // exit edit mode. Not called on failure — failure
  // keeps the field in edit mode so the user can retry.
  onSuccess?: () => void
  // Called with the server's error map when the request
  // returns 422. The caller decides how to surface it
  // (inline message vs toast). Falsy on success.
  onError?: (errors: Record<string, string>) => void
}

interface UseDealUpdateResult {
  submitting: boolean
  submit: (args: SubmitArgs) => void
}

interface SubmitArgs {
  // The change applied to the optimistic deal prop.
  // Anything other than `value` should be present, e.g.
  // `{ name: "Renewal" }`.
  optimisticPatch: Partial<DealDrawerData>
  // The shape sent to the server. Keys match the form
  // field names — `close_date` (snake_case), not
  // `closeDate`.
  payload: DealUpdatePayload
}

export function useDealUpdate({
  deal,
  dealPropKey,
  onSuccess,
  onError,
}: UseDealUpdateOptions): UseDealUpdateResult {
  const [submitting, setSubmitting] = useState(false)
  // Synchronous in-flight guard. `submitting` lives in
  // React state, which means a same-tick second tap reads
  // the pre-render value (still false) and slips through.
  // The ref flips before the fetch dispatches so back-to-
  // back taps collapse to a single PATCH.
  const inFlight = useRef(false)

  const submit = useCallback(
    ({ optimisticPatch, payload }: SubmitArgs) => {
      if (inFlight.current) return
      inFlight.current = true

      const previousDeal = deal
      const optimisticDeal: DealDrawerData = {
        ...deal,
        ...optimisticPatch,
      }

      replaceDealProp(dealPropKey, () => optimisticDeal)

      setSubmitting(true)
      fetch(`/v2/deals/${deal.id}`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ deal: payload }),
      })
        .then(async (response) => {
          if (response.status === 204) {
            onSuccess?.()
            return
          }

          // 422 — surface field errors and revert the
          // optimistic patch so the user sees the
          // pre-edit value next to the message.
          if (response.status === 422) {
            const body = await safeJson(response)
            const errors =
              (body?.errors as Record<string, string>) ?? {}
            replaceDealProp(dealPropKey, () => previousDeal)
            onError?.(errors)
            return
          }

          throw new Error(`HTTP ${response.status}`)
        })
        .catch(() => {
          replaceDealProp(dealPropKey, () => previousDeal)
          onError?.({
            base: "Something went wrong. Please try again.",
          })
        })
        .finally(() => {
          inFlight.current = false
          setSubmitting(false)
        })
    },
    [deal, dealPropKey, onError, onSuccess],
  )

  return { submitting, submit }
}

async function safeJson(response: Response) {
  try {
    return await response.json()
  } catch {
    return null
  }
}

// Helpers that callers use to build the optimistic
// patch. Co-located so each editor doesn't reinvent
// the formatting.

export function formatCurrencyDisplay(
  amount: number | null,
): string | null {
  if (amount == null || Number.isNaN(amount)) return null

  // Match the Ruby `number_to_currency` default with
  // `strip_insignificant_zeros: true`. Whole-dollar
  // amounts render without ".00".
  const fractionDigits = Number.isInteger(amount) ? 0 : 2
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: 2,
  })
}

export function formatCloseAtDisplay(
  iso: string | null,
): string | null {
  if (!iso) return null

  // Parse as a plain date (ignore time-zone) so we don't
  // shift "2026-06-01" to "May 31" in a negative-offset
  // timezone. Ruby ships this as a Date.iso8601 string.
  const [year, month, day] = iso.split("-").map(Number)
  if (!year || !month || !day) return null

  const date = new Date(Date.UTC(year, month - 1, day))
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  })
}

